import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import debounce from 'lodash/debounce';

import { getCompletedTopicsAndItemsContent, showFilterValues } from 'Shared/selectors/workspace';
import { fetchAdditionalItems } from 'Shared/actions/todos';
import { SelectorValue } from 'Shared/constants/types';
import Spinner from 'Shared/components/Spinner';
import CompletedItem from 'Dashboard/components/CompletedItem/CompletedItem';
import FilterSelector from 'Shared/components/FilterSelector/FilterSelector';
import { WORKSPACE_CONTENT_CONTAINER_ID } from 'Shared/constants/constants';
import { sortFilterValues } from 'Shared/utils/sorting';
import { WpContainer, WpContainerHeader, WpContainerBody } from 'Wpds/components/container';

import CompletedChildrensList from '../CompletedChildrensList/CompletedChildrensList';

import './CompletedTopicsAndItemsList.scss';

const showFilterValuesOptions = Object.values<SelectorValue>(showFilterValues);
const sortFilterValuesOptions = Object.values<SelectorValue>(sortFilterValues);

type Props = {
  focusedItemUuid: string;
};

function CompletedTopicsAndItemsList({ focusedItemUuid }: Props) {
  const dispatch = useDispatch();
  const [showFilterValue, setShowFilterValue] = useState(showFilterValuesOptions[0]);
  const [sortFilterValue, setSortFilterValue] = useState(sortFilterValuesOptions[0]);
  const workspaceId = useSelector(state => state.workspace.id);
  const [itemsCount, setItemsCount] = useState(0);
  const [currentOffset, setCurrenOffset] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const handleShowFilterChange = value => setShowFilterValue(value);
  const handleSortFilterChange = value => setSortFilterValue(value);

  const fetchAdditionalCompletedItems = useCallback(async () => {
    if (!loaded) setLoaded(true);
    const count = await dispatch(
      fetchAdditionalItems({ workspaceId, forcedUuid: focusedItemUuid, filter: 'completed', offset: currentOffset })
    );
    if (itemsCount !== count) setItemsCount(count || 0);
    setCurrenOffset(currentOffset + 20);
  }, [loaded, dispatch, workspaceId, focusedItemUuid, currentOffset, itemsCount]);

  const debounceFetchAdditionalItems = useCallback(debounce(fetchAdditionalCompletedItems, 1000), [
    fetchAdditionalCompletedItems,
  ]);

  const parentItems = useSelector(getCompletedTopicsAndItemsContent(showFilterValue, sortFilterValue));
  const countCompletedItems = useSelector(
    state =>
      [...state.workspace.meeting_items, ...state.workspace.discussion_topics].filter(({ completed }) => completed)
        .length
  );
  const hasMoreFeedbacks = !loaded || countCompletedItems < itemsCount;

  return (
    <WpContainer>
      <WpContainerHeader>
        <div className="d-flex justify-content-between align-items-center">
          <FilterSelector
            label="SHOW:"
            value={showFilterValue}
            options={showFilterValuesOptions}
            onChange={handleShowFilterChange}
          />
          <FilterSelector
            label="SORT BY:"
            value={sortFilterValue}
            options={sortFilterValuesOptions}
            onChange={handleSortFilterChange}
          />
        </div>
      </WpContainerHeader>
      <WpContainerBody>
        <div className="completed-topics-and-items-list d-flex flex-column">
          <InfiniteScroll
            pageStart={0}
            loadMore={debounceFetchAdditionalItems}
            hasMore={hasMoreFeedbacks}
            threshold={10}
            loader={<Spinner key="scroll-spinner" activeIf className="mt-5" />}
            useWindow={false}
            getScrollParent={() => document.getElementById(WORKSPACE_CONTENT_CONTAINER_ID)}
          >
            {parentItems.map(({ uuid }) => (
              <div key={uuid}>
                <CompletedItem uuid={uuid} isFocused={focusedItemUuid === uuid} />
                <div className="ml-4">
                  <CompletedChildrensList parentUuid={uuid} focusedItemUuid={focusedItemUuid} />
                </div>
              </div>
            ))}
          </InfiniteScroll>
        </div>
      </WpContainerBody>
    </WpContainer>
  );
}

export default CompletedTopicsAndItemsList;
